import { useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { api } from '@/lib/api.js';
import { Button } from '@/components/ui/Button.jsx';
import { Input, FormField } from '@/components/ui/Input.jsx';
import { useToast } from '@/components/ui/Toast.jsx';

export default function OTRatesPage() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const { data, isLoading } = useQuery({
    queryKey: ['companySettings'],
    queryFn: () => api.get('/settings/company'),
  });

  const { register, handleSubmit, reset } = useForm({
    defaultValues: {
      ot_default_multiplier: 1.5,
      holiday_ot_multiplier: 2.0,
      ot_triple_multiplier: 3.0,
    },
  });

  useEffect(() => {
    if (data) {
      reset({
        ot_default_multiplier: data.ot_default_multiplier ?? 1.5,
        holiday_ot_multiplier: data.holiday_ot_multiplier ?? 2.0,
        ot_triple_multiplier: data.ot_triple_multiplier ?? 3.0,
      });
    }
  }, [data]); // eslint-disable-line

  const save = useMutation({
    mutationFn: (v) =>
      api.put('/settings/company', {
        ...data,
        ot_default_multiplier: Number(v.ot_default_multiplier),
        holiday_ot_multiplier: Number(v.holiday_ot_multiplier),
        ot_triple_multiplier: Number(v.ot_triple_multiplier),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['companySettings'] });
      toast({ variant: 'success', title: 'OT rates saved' });
    },
    onError: (e) => toast({ variant: 'error', title: 'Save failed', description: e.message }),
  });

  if (isLoading) return null;

  return (
    <form onSubmit={handleSubmit((v) => save.mutate(v))} className="space-y-5">
      <p className="text-sm text-slate-500 dark:text-slate-400">
        Multipliers used for auto-derived overtime (weekday, Saturday/Sunday holiday types) and
        payroll runs. Manual OT entries carry their own rate per record.
      </p>

      <div className="grid gap-4 sm:grid-cols-3">
        <FormField label="Single OT (×)" hint="Weekday extra hours, default 1.5">
          <Input type="number" step="0.1" min="1" {...register('ot_default_multiplier')} />
        </FormField>
        <FormField label="Double OT (×)" hint="Public holidays &amp; 'double' Sat/Sun, default 2.0">
          <Input type="number" step="0.1" min="1" {...register('holiday_ot_multiplier')} />
        </FormField>
        <FormField label="Triple OT (×)" hint="'Triple' Sat/Sun type, default 3.0">
          <Input type="number" step="0.1" min="1" {...register('ot_triple_multiplier')} />
        </FormField>
      </div>

      <div className="flex justify-end">
        <Button type="submit" disabled={save.isPending}>
          {save.isPending ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </form>
  );
}
